import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });
}

function normalizeDeliveries(data) {
  if (Array.isArray(data?.deliveries)) return data.deliveries;
  if (data?.deliveries && typeof data.deliveries === 'object') {
    return Object.entries(data.deliveries).map(([destination, value]) => ({
      destination,
      ...(typeof value === 'string' ? { status: value } : value),
    }));
  }
  if (!data?.integrations) return [];
  return Object.entries(data.integrations).map(([destination, value]) => ({ destination, ...(typeof value === 'string' ? { status: value } : value) }));
}

export default function Completion({ userData, completionData, onReset }) {
  const eventId = completionData?.eventId || completionData?.event_id;
  const [record, setRecord] = useState(completionData || null);
  const [pollError, setPollError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!eventId) return undefined;
    let active = true;
    let timer;

    const poll = async () => {
      try {
        const { data } = await axios.get(`/api/completions/${encodeURIComponent(eventId)}/status`, { withCredentials: true });
        if (!active) return;
        setRecord(data.completion || data);
        setPollError('');
        const deliveries = normalizeDeliveries(data.completion || data);
        const pending = deliveries.some(item => ['pending', 'queued', 'retry', 'retrying', 'processing'].includes(String(item.status).toLowerCase()));
        if (pending) timer = window.setTimeout(poll, 5000);
      } catch (error) {
        if (!active) return;
        setPollError('Delivery status is temporarily unavailable. Your certificate remains securely queued.');
        timer = window.setTimeout(poll, 10000);
      }
    };
    poll();
    return () => { active = false; if (timer) window.clearTimeout(timer); };
  }, [eventId]);

  const deliveries = useMemo(() => normalizeDeliveries(record), [record]);
  const successful = deliveries.filter(item => ['sent', 'success', 'completed', 'delivered'].includes(String(item.status).toLowerCase()));
  const failed = deliveries.filter(item => item.status === 'dead');
  const pending = deliveries.filter(item => ['pending', 'queued', 'retry', 'retrying', 'processing'].includes(String(item.status).toLowerCase()));
  const certificateId = record?.certificateId || record?.certificate_id || completionData?.certificateId;
  const completionDate = record?.completedAt || record?.completionDate || record?.completed_at || completionData?.completedAt;
  const scorePercent = record?.scorePercent ?? completionData?.scorePercent;

  const downloadCertificate = async (documentType = 'certificate') => {
    if (!eventId) return;
    setDownloading(true);
    try {
      const response = await axios.get(`/api/completions/${encodeURIComponent(eventId)}/${documentType}`, { responseType: 'blob', withCredentials: true });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `HIPAA-${documentType}-${userData.fullName.replace(/\s+/g, '-')}.pdf`;
      anchor.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (_) {
      window.alert('The certificate could not be downloaded right now. Please retry; the secure copy remains on file.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="page-container">
      <div style={{ marginBottom: 24 }}><div className="step-badge"><span className="step-badge-dot" />Step 5 of 5 — Complete</div></div>
      <div className="card">
        <div className="completion-hero">
          <div className="completion-checkmark">✓</div>
          <h1 className="completion-title">Training Complete!</h1>
          <p className="completion-subtitle">Congratulations, <strong>{userData.fullName}</strong>.<br />Your verified completion and both PDF documents are securely recorded.</p>
        </div>

        <div className="email-notice">
          <span className="email-notice-icon">{pending.length ? '⏳' : '📧'}</span>
          <div>
            {pending.length ? (
              <><strong>Automatic delivery is in progress.</strong> Your completion is saved. You can close this page while delivery to the configured destinations continues.</>
            ) : (
              <><strong>Your training record is saved.</strong> {failed.length ? 'One or more document deliveries need administrator attention. Your signed originals remain safe and available below.' : successful.length ? 'Delivery results are shown below. Email provider acceptance does not confirm inbox delivery.' : 'Download both documents below. You can also open your documents from your signed-in OdyHR account.'}</>
            )}
          </div>
        </div>

        <div className="cert-details-grid">
          {[
            { label: 'Employee Name', value: userData.fullName },
            { label: 'Assigned Role', value: userData.role },
            { label: 'Phone', value: userData.phone },
            { label: 'Date Completed', value: formatDate(completionDate) },
            { label: 'Certificate ID', value: certificateId },
            { label: 'Final Score', value: scorePercent == null ? 'Passed' : `${scorePercent}%` },
            { label: 'Verification', value: '5 standard videos + role video + passing quiz ✓' },
          ].map(({ label, value }) => <div key={label} className="cert-detail-item"><div className="cert-detail-label">{label}</div><div className="cert-detail-value">{value || '—'}</div></div>)}
        </div>

        {deliveries.length > 0 && (
          <div style={{ background: '#EEF3F8', border: '1px solid #CFD8DC', borderRadius: 8, padding: '14px 18px', marginBottom: 20 }}>
            <strong style={{ color: '#1B3A5C', fontSize: 13 }}>Automatic delivery status</strong>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              {deliveries.map(item => (
                <span key={item.destination} style={{ background: '#fff', border: '1px solid #DDE3ED', borderRadius: 14, padding: '5px 10px', fontSize: 12, color: '#546E7A' }}>
                  {{trinity_hr:'HR filing',drive_certificate:'Certificate archive',drive_acknowledgement:'Acknowledgment archive',email:'Email',sheets:'Tracking archive'}[item.destination] || item.destination}: <strong>{item.destination === 'email' && item.status === 'delivered' ? 'Accepted by email provider' : item.status === 'dead' ? 'Administrator attention needed' : item.status}</strong>
                </span>
              ))}
            </div>
          </div>
        )}

        {pollError && <div style={{ color: '#975A16', background: '#FFFAF0', border: '1px solid #FBD38D', borderRadius: 7, padding: '10px 14px', fontSize: 13, marginBottom: 18 }}>{pollError}</div>}

        <div className="cert-actions">
          <button className="btn btn-primary btn--lg" onClick={() => downloadCertificate('certificate')} disabled={!eventId || downloading}>{downloading ? 'Preparing PDF…' : '⬇️ Download Verified Certificate'}</button>
          <button className="btn btn-outline" disabled={!eventId || downloading} onClick={() => downloadCertificate('acknowledgement')}>Download Signed Acknowledgment</button>
          <button className="btn btn-outline" onClick={onReset} style={{ marginLeft: 'auto' }}>Finish &amp; Sign Out</button>
        </div>
      </div>

      <div style={{ marginTop: 24, padding: '20px 28px', background: '#fff', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderLeft: '4px solid #00A3A3' }}>
        <p style={{ fontSize: 14, color: '#2C3E50', lineHeight: 1.7 }}><strong>Thank you for completing your HIPAA training.</strong> Certificate ID <strong>{certificateId || 'pending'}</strong> is the immutable reference for this completion.</p>
      </div>
    </div>
  );
}
